/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import Highlighter from 'react-highlight-words';

// Component imports
import { Button, Col, Grid, Input, Modal, Popconfirm, Row, Space, Spin, Table, Tooltip, Typography } from 'antd';
import { LoadingOutlined, SearchOutlined } from '@ant-design/icons';
import { TrainingIcon } from '../Icons/TrainingIcon';
import NlpPipelineForm from '../Forms/NlpPipelineForm';
import NlpPipelineStatusTag from '../UI/NlpPipelineStatusTag';
import Permissioned from '../Permissioned';
import ReadMorePopover from '../UI/ReadMorePopover';
import SideModal from '../Layout/SideModal';
import { EditIcon } from '../Icons/EditIcon';
import { DeleteIcon } from '../Icons/DeleteIcon';
import { PlusIcon } from '../Icons/PlusIcon';

// Misc imports
import { LANGUAGE_OPTIONS, NLP_TYPES, PERMISSIONS_GROUP_ADMIN, DEFAULT_DATETIME_FORMAT, DEFAULT_PAGINATION_SIZE } from '../../constants/main';
import { truncate } from '../../utils/string/truncate';
import { PERMISSIONS_GROUP_EDITOR, PERMISSIONS_GROUP_VIEWER } from '../../constants/main';
import { useDeleteNlpPipelineMutation, useFetchNlpPipelinesQuery, useTrainNlpPipelineMutation } from '../../store/nlpPipelines/nlpPipelinesApi';
import { NLP_PIPELINE_STATUSES } from '../../constants/main';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import { useModalConfirm } from '../../utils/hooks/useModalConfirm';
import { useDragDetection } from '../../utils/hooks/useDragDetection';
import { useLazyFetchBotQuery } from '../../store/bots/botsApi';
import useUserPermissions from '../../utils/hooks/useUserPermissions';
import useTableFilterIcon from '../../utils/hooks/useTableFilterIcon';
import { API_NLP_SERVICE_URL } from '../../constants/env';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { PLUGIN_CATEGORY } from '../../constants/integrations';

// Constant declarations
const { Text } = Typography;
const { useBreakpoint } = Grid;

const NlpPipelinesTable = () => {
  const { tenantId, orgId } = useParams();
  const { t } = useTranslation();
  const { botLanguages } = useBotLanguage();
  const userPermissions = useUserPermissions(orgId, tenantId);
  const screens = useBreakpoint();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobileSize = screens.xs;
  const searchParams = new URLSearchParams(location.search);
  const { handleMouseDown, isDragging } = useDragDetection();

  // Store state
  const { data: bot } = getCurrentQueryState('fetchBot', { orgId, tenantId });
  const { data: nlpPipelines, isLoading: isLoadingNlpPipelines, refetch: fetchNlpPipelines } = useFetchNlpPipelinesQuery({ orgId, tenantId });
  const [deleteNlpPipeline] = useDeleteNlpPipelineMutation({ fixedCacheKey: 'delete-nlp-pipeline-shared' });
  const [trainNlpPipeline, { isLoading: isTrainingPipeline, isSuccess: isTrainingPipelineSuccessful, reset: resetIsTrainingPipeline, isError: isTrainingPipelineError }] = useTrainNlpPipelineMutation({ fixedCacheKey: 'training-nlp-shared' });
  const [fetchBot] = useLazyFetchBotQuery({});

  // Local state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false); // The modal to create a new pipeline
  const [isEditModalOpen, setIsEditModalOpen] = useState(false); // The modal to edit a pipeline
  const [nlpPipelineId, setNlpPipelineId] = useState(searchParams.get('pipelineId')); // The id of the pipeline to edit, set when clicking on the edit button
  const [isTrainingModalOpen, setIsTrainingModalOpen] = useState(false); // The modal to show the training status of the pipeline
  const [searchFilter, setSearchFilter] = useState(''); // The search input value
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [preventFormModalConfirm, setPreventFormModalConfirm] = useState(false);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGINATION_SIZE);
  const { showModalConfirm } = useModalConfirm(hasPendingChanges);
  const nlpRef = useRef();

  const isPipelineFromSemanticSearchNodePlugin = (nlpId) => {
    return nlpId === Object.values(bot?.plugins || [])?.find(plugin => plugin.category === PLUGIN_CATEGORY.LLM_SEMANTIC_SEARCH_NODE)?.settings?.nlpLocalId;
  };

  // Handle NLP training responses
  useEffect(() => {
    if (isTrainingPipeline && !isTrainingPipelineSuccessful) {
      setIsTrainingModalOpen(true);
    }

    if (isTrainingPipelineSuccessful) {
      setIsTrainingModalOpen(false);
      resetIsTrainingPipeline();
    }

    if (isTrainingPipelineError) {
      setIsTrainingModalOpen(false);
      resetIsTrainingPipeline();
    }
  }, [isTrainingPipeline, isTrainingPipelineSuccessful, isTrainingPipelineError]);

  // If there's a pipeline ID in the URL, open the edit modal. If not, close it.
  useEffect(() => {
    setIsEditModalOpen(!!nlpPipelineId);
  }, [nlpPipelineId]);

  const columns = [
    {
      title: t('NLP Provider'),
      dataIndex: 'type',
      key: 'type',
      filters: Object.values(NLP_TYPES)
        .filter(provider => provider !== NLP_TYPES.LEX && provider !== NLP_TYPES.WIT) // Hide Lex and Wit options
        .map(provider => ({
          text: t(provider, { context: 'nlp provider' }),
          value: provider
        })),
      onFilter: (value, record) => record.type.toLowerCase() === value.toLowerCase(),
      filterIcon: useTableFilterIcon,
      render: (_text, record) => {
        return t(record.type, { context: 'nlp provider' });
      }
    },
    {
      title: t('Name'),
      dataIndex: 'name',
      key: 'name',
      render: (_text, record) => (
        <Highlighter
          highlightClassName="bg-highlight p-0"
          searchWords={[searchFilter]}
          autoEscape
          textToHighlight={(record.name)}
        />
      )
    },
    {
      title: t('Description'),
      dataIndex: 'description',
      key: 'description',
      responsive: ['md'],
      render: (_text, record) => {
        const { description } = record;
        return description ? (
          <Fragment>
            <Highlighter
              highlightClassName="bg-highlight p-0"
              searchWords={[searchFilter]}
              autoEscape
              textToHighlight={truncate(description, 50)}
            />

            {
              description.length > 50 ? (
                <ReadMorePopover
                  message={description}
                  searchFilter={searchFilter}
                  buttonClassName="ml-2"
                />
              ) : null
            }
          </Fragment>
        ) : null;
      }
    },
    {
      title: t('Language'),
      dataIndex: 'language',
      key: 'language',
      filters: botLanguages.map(language => ({
        text: LANGUAGE_OPTIONS.find(lang => lang.key === language).label,
        value: language
      })),
      onFilter: (value, record) => record.language.toLowerCase() === value.toLowerCase(),
      filterIcon: useTableFilterIcon,
      render: (_text, record) => {
        const language = LANGUAGE_OPTIONS.find(language => language.key === record.language);
        return language ? language.label : null;
      }
    },
    {
      title: t('Date Created'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      responsive: ['lg'],
      sorter: (a, b) => a.createdAt - b.createdAt,
      render: (_text, record) => {
        if (record.createdAt) {
          return moment(record.createdAt).format(DEFAULT_DATETIME_FORMAT);
        } else {
          return '-';
        }
      }
    },
    {
      title: t('Date Updated'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      responsive: ['xxl'],
      sorter: (a, b) => a.updatedAt - b.updatedAt,
      render: (_text, record) => {
        if (record.updatedAt) {
          return moment(record.updatedAt).format(DEFAULT_DATETIME_FORMAT);
        } else {
          return '-';
        }
      }
    },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      responsive: ['md'],
      filters: Object.values(NLP_PIPELINE_STATUSES).map(status => ({
        text: t(status, { context: 'nlp pipeline status' }),
        value: status
      })),
      onFilter: (value, record) => record.status.toLowerCase() === value.toLowerCase(),
      filterIcon: useTableFilterIcon,
      render: (_text, record) => <NlpPipelineStatusTag pipelineStatus={record.status} />
    },
    {
      title: t('Retrain Count'),
      dataIndex: 'trainCount',
      key: 'trainCount',
      align: 'center'
    },
    {
      title: t('Actions'),
      key: 'actions',
      fixed: 'right',
      align: 'center',
      width: 140,
      hidden: !userPermissions.some(permission => PERMISSIONS_GROUP_VIEWER.includes(permission)),
      render: record => {
        if (userPermissions.some(permission => PERMISSIONS_GROUP_EDITOR.includes(permission))) {
          return (
            <Space onClick={(e) => e.stopPropagation()} size="small">
              <Tooltip
                overlayClassName="pointer-events-none"
                title={t('Train System')}
              >
                <Button
                  shape="circle"
                  type="text"
                  icon={<TrainingIcon />}
                  onClick={() => {
                    onTrain(record.id);
                  }}
                  disabled={record.status === NLP_PIPELINE_STATUSES.TRAINING || record.status === NLP_PIPELINE_STATUSES.SYNCING}
                />
              </Tooltip>

              <Tooltip overlayClassName="pointer-events-none" title={t('Edit System')}>
                <Button
                  shape="circle"
                  type="text"
                  icon={<EditIcon />}
                  disabled={isPipelineFromSemanticSearchNodePlugin(record.id)}
                  onClick={() => {
                    openEditModal(record.id);
                  }}
                />
              </Tooltip>

              <Popconfirm
                disabled={isPipelineFromSemanticSearchNodePlugin(record.id)}
                title={
                  <Fragment>
                    <Text className="fw-bold">
                      {t('Delete System?')}<br/>
                    </Text>
                    <Text>
                      {t('The NLP System will be permanently deleted')}<br/>
                      {nlpPipelines.items.length === 1 ? t('and your automated answers will not work.') : null}
                    </Text>
                  </Fragment>
                }
                okText={t('Delete')}
                cancelText={t('Cancel')}
                placement="topRight"
                onConfirm={() => {
                  onDelete(record.id);
                }}
              >
                <Tooltip overlayClassName="pointer-events-none" title={t('Delete System')}>
                  <Button
                    shape="circle"
                    type="text"
                    icon={<DeleteIcon />}
                    disabled={isPipelineFromSemanticSearchNodePlugin(record.id)}
                  />
                </Tooltip>
              </Popconfirm>
            </Space>
          );
        }
      }
    }
  ];

  const onTrain = async (nlpPipelineId) => {
    let forceFullRetrain = false;
    if (nlpPipelines.items.find((pipeline) => pipeline.id === nlpPipelineId).status === NLP_PIPELINE_STATUSES.READY) {
      forceFullRetrain = true;
    }
    const isSuccess = await trainNlpPipeline({
      orgId,
      tenantId,
      nlpPipelineId,
      urlParams: { forceFullRetrain: forceFullRetrain },
      ...bot?.systemSettings?.nlpService === 'HBF_NLP' && { apiUrl: API_NLP_SERVICE_URL }
    }).unwrap();

    if (isSuccess) {
      // Start polling for nlp pipeline status by refetching nlp pipelines until all pipelines are trained
      fetchNlpPipelines();
      const intervalId = setInterval(async () => {
        const nlpPipelines = await fetchNlpPipelines({ orgId, tenantId }).unwrap();

        if (!(nlpPipelines?.items || []).some(pipeline => pipeline.status === NLP_PIPELINE_STATUSES.TRAINING || pipeline.status === NLP_PIPELINE_STATUSES.SYNCING)) {
          clearInterval(intervalId);
          fetchBot({ orgId, tenantId });
        } else {
          fetchNlpPipelines();
        }
      }, 5000);
    }
  };

  const onDelete = async (nlpPipelineId) => {
    await deleteNlpPipeline({ orgId, tenantId, nlpPipelineId });
  };

  const openCreateModal = () => {
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const onModalCancel = (closeModalFunction) => {
    showModalConfirm({
      defaultAction: () => {
        closeModalFunction();
        setHasPendingChanges(false);
      },
      onConfirm: async () => {
        const formValues = nlpRef?.current?.form?.getFieldsValue(true);
        await nlpRef?.current?.onSubmit(formValues);
        closeModalFunction();
        setHasPendingChanges(false);
      },
      onMaskClick: () => {
        setPreventFormModalConfirm(false);
      },
      okText: t('Save Changes'),
      cancelText: t('Quit without saving'),
      title: t('Save Changes?'),
      content: t('Your unsaved changes will be lost. Save changes before closing?')
    });
  };

  const openEditModal = (pipelineId) => {
    setNlpPipelineId(pipelineId);
    searchParams.set('pipelineId', pipelineId);

    navigate({
      pathname: location.pathname,
      search: searchParams.toString()
    });
  };

  const closeEditModal = () => {
    setNlpPipelineId(null); // This will also close the modal
    searchParams.delete('pipelineId');
    navigate({
      pathname: location.pathname,
      search: searchParams.toString()
    });
  };

  const handleTableChange = (pagination, _filters, _sorter) => {
    setPageSize(pagination.pageSize);
  };

  return (
    <Fragment>
      <Row gutter={24} wrap={isMobileSize}>
        <Col flex="auto" className="mb-2">
          <Input
            size="large"
            style={{ maxWidth: isMobileSize ? '100%' : 280 }}
            placeholder={t('Search in contents')}
            allowClear
            prefix={<SearchOutlined className="text-muted" />}
            className="w-100"
            onChange={(e) => {
              setSearchFilter(e.target.value);
            }}
          />
        </Col>

        <Col
          flex={isMobileSize ? 'auto' : 'none'}
          className="mb-2"
        >
          <Permissioned
            allowedPermissions={PERMISSIONS_GROUP_ADMIN}
            currentPermissions={userPermissions}
            fallbackComponent={null}
          >
            <Button
              icon={<PlusIcon />}
              type="primary"
              className="float-right mb-2"
              size="large"
              block={isMobileSize}
              onClick={openCreateModal}
            >
              {t('Connect NLP System')}
            </Button>
          </Permissioned>
        </Col>
      </Row>

      <SideModal
        title={t('Create NLP System')}
        open={isCreateModalOpen}
        destroyOnClose
        onClose={() => {
          onModalCancel(closeCreateModal);
        }}
        bodyClassName="p-0"
        width={900}
      >
        <NlpPipelineForm
          ref={nlpRef}
          setPreventModalConfirm={setPreventFormModalConfirm}
          disableConfirm={preventFormModalConfirm}
          onSuccess={closeCreateModal}
          onCancel={closeCreateModal}
          setHasPendingChanges={setHasPendingChanges}
          bodyClassName="px-6 pt-6 flex-grow-1"
          footerClassName="ant-drawer-footer"
        />
      </SideModal>

      <SideModal
        title={t('Edit NLP System')}
        open={isEditModalOpen}
        destroyOnClose
        onClose={() => {
          setPreventFormModalConfirm(true);
          onModalCancel(closeEditModal);
        }}
        bodyClassName="p-0"
        width={900}
      >
        <NlpPipelineForm
          ref={nlpRef}
          setPreventModalConfirm={setPreventFormModalConfirm}
          disableConfirm={preventFormModalConfirm}
          id={nlpPipelineId}
          isUpdateMode={true}
          onCancel={closeEditModal}
          setHasPendingChanges={setHasPendingChanges}
          bodyClassName="px-6 pt-6 flex-grow-1"
          footerClassName="ant-drawer-footer"
        />
      </SideModal>

      <Modal
        title={t('Training NLP System')}
        open={isTrainingModalOpen}
        onCancel={() => setIsTrainingModalOpen(false)}
        footer={null}
        style={{ minWidth: 320 }}
        width={'20%'}
        className="my-5"
        centered
      >
        <div className="text-center">
          <Text className="d-block mb-3">{t('Processing, please wait')}...</Text>
          <Spin indicator={<LoadingOutlined className="fs-xxl" spin />} />
        </div>
      </Modal>

      <Table
        rowKey="id"
        size="small"
        onRow={(record) => ({
          onMouseDown: handleMouseDown,
          onClick: () => {
            if(isPipelineFromSemanticSearchNodePlugin(record.id)) {
              return;
            }

            if(!isDragging() && userPermissions.some(permission => PERMISSIONS_GROUP_EDITOR.includes(permission))) {
              openEditModal(record.id);
            }
          }
        })}
        rowClassName="cursor-pointer"
        pagination={{
          size: 'default',
          pageSize: pageSize,
          showSizeChanger: true,
          showTotal: (total) => `${t('Total Systems')}: ${total}`
        }}
        dataSource={nlpPipelines?.items.filter(i => i.name.toLowerCase().includes(searchFilter.toLowerCase()) || i.description?.toLowerCase().includes(searchFilter.toLowerCase()))}
        columns={columns}
        loading={isLoadingNlpPipelines}
        onChange={handleTableChange}
      />
    </Fragment>
  );
};

export default NlpPipelinesTable;
